export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

/** Returns true if the platform modifier key is held (Cmd on Mac, Ctrl elsewhere). */
export function isModKey(e: KeyboardEvent): boolean {
  return isMac ? e.metaKey : e.ctrlKey;
}

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

/** A registered shortcut that has a human-readable label for display. */
export interface ShortcutEntry {
  /** The original shortcut string as passed to `register`, e.g. 'mod+k'. */
  shortcut: string;
  /** Human-readable name shown in the command palette shortcuts list. */
  label: string;
}

export class KeyboardShortcutManager {
  private shortcuts = new Map<string, () => void>();
  private labels = new Map<string, ShortcutEntry>();
  private isListening = false;

  parseShortcut(shortcut: string): KeyboardShortcut {
    const parts = shortcut.toLowerCase().split('+').map(s => s.trim());
    const result: KeyboardShortcut = { key: '' };

    for (const part of parts) {
      switch (part) {
        case 'ctrl':
        case 'control':
          result.ctrlKey = true;
          break;
        case 'mod':
          if (isMac) {
            result.metaKey = true;
          } else {
            result.ctrlKey = true;
          }
          break;
        case 'cmd':
        case 'meta':
          result.metaKey = true;
          break;
        case 'shift':
          result.shiftKey = true;
          break;
        case 'alt':
        case 'option':
          result.altKey = true;
          break;
        default:
          result.key = part;
      }
    }

    return result;
  }

  normalizeShortcut(shortcut: KeyboardShortcut): string {
    const parts: string[] = [];

    if (shortcut.ctrlKey) parts.push('ctrl');
    if (shortcut.metaKey) parts.push('meta');
    if (shortcut.shiftKey) parts.push('shift');
    if (shortcut.altKey) parts.push('alt');
    if (shortcut.key) parts.push(shortcut.key);

    return parts.join('+');
  }

  register(shortcut: string, handler: () => void, label?: string): () => void {
    const normalized = this.normalizeShortcut(this.parseShortcut(shortcut));
    this.shortcuts.set(normalized, handler);

    if (label) {
      this.labels.set(normalized, { shortcut, label });
    }

    if (!this.isListening) {
      this.startListening();
    }

    return () => {
      this.shortcuts.delete(normalized);
      this.labels.delete(normalized);
      if (this.shortcuts.size === 0) {
        this.stopListening();
      }
    };
  }

  /** Returns all shortcuts registered with a human-readable label. */
  getRegisteredShortcuts(): ShortcutEntry[] {
    return Array.from(this.labels.values());
  }

  private startListening(): void {
    if (this.isListening) return;

    document.addEventListener('keydown', this.handleKeyDown);
    this.isListening = true;
  }

  private stopListening(): void {
    if (!this.isListening) return;

    document.removeEventListener('keydown', this.handleKeyDown);
    this.isListening = false;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Don't trigger shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const shortcut: KeyboardShortcut = {
      key: event.key.toLowerCase(),
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
    };

    const normalized = this.normalizeShortcut(shortcut);
    const handler = this.shortcuts.get(normalized);

    if (handler) {
      event.preventDefault();
      event.stopPropagation();
      handler();
    }
  };

  destroy(): void {
    this.shortcuts.clear();
    this.labels.clear();
    this.stopListening();
  }
}

// Global shortcut manager instance
export const keyboardShortcutManager = new KeyboardShortcutManager();

// Utility function to format shortcuts for display
export function formatShortcut(shortcut: string): string {
  const parsed = new KeyboardShortcutManager().parseShortcut(shortcut);
  const parts: string[] = [];

  // check platform support before using ⊞ for windows
  if (parsed.metaKey) parts.push(isMac ? '⌘' : 'win');
  if (parsed.ctrlKey) parts.push('ctrl');
  if (parsed.shiftKey) parts.push('⇧');
  if (parsed.altKey) parts.push(isMac ? '⌥' : 'alt');
  if (parsed.key) {
    const keySymbols: Record<string, string> = {
      enter: '↵',
      return: '↵',
      escape: 'esc',
      backspace: '⌫',
      delete: '⌦',
      tab: '⇥',
      arrowup: '↑',
      arrowdown: '↓',
      arrowleft: '←',
      arrowright: '→',
    };
    const sym = keySymbols[parsed.key];
    parts.push(sym ?? parsed.key.charAt(0).toUpperCase() + parsed.key.slice(1));
  }

  return parts.join(' ');
}